'use client';
import React, { ErrorInfo } from 'react';

import * as Sentry from '@sentry/browser';

import InternalServerError, {
  ServerErrorProps,
} from '@components/utils/internal-server-error';

const IS_PROD = process.env.NEXT_PUBLIC_BUILD_TYPE === 'PRODUCTION';

interface PropsTypes {
  children?: React.ReactNode;
  shouldDisplay500Page?: boolean;
}

class ErrorBoundary extends React.Component<PropsTypes, ServerErrorProps> {
  constructor(props) {
    super(props);
    this.state = { error: null, eventId: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.is404(error)) {
      console.warn('404 error ErrorBoundary skipped.');
      return;
    }

    if (IS_PROD) {
      try {
        Sentry.withScope((scope) => {
          scope.setExtras(errorInfo as any);
          const eventId = Sentry.captureException(error);
          this.setState({
            ...this.state,
            eventId: eventId,
          });
        });
      } catch (e) {
        console.error('Sentry Error:', e);
      }
    }

    console.error('ErrorBoundary Error:', { error, errorInfo });

    this.setState({ error });
  }

  is404(error: Error): boolean {
    return error?.message?.includes('404');
  }

  render() {
    const { error } = this.state;

    if (error && this.is404(error)) {
      return this.props?.children || null;
    }

    if (error && this.props.shouldDisplay500Page) {
      return <InternalServerError {...this.state} />;
    }

    return this.props?.children;
  }
}

export default ErrorBoundary;
